import React, { useEffect, useState } from 'react';
import DashboardModeHeader from 'components/common/dashboard_mode_header';
import { convertPercentToDecimal } from 'utilities/search_parameters';
import {
  getInitialProgressPercentageNumber,
  getProgressPercentageNumber,
  isDashboardMode,
  isSourceQsrHardwareFlow,
} from 'helpers/search_parameter_helpers';
import { Box } from '@mui/material';
import { useLocation, useHistory } from 'react-router-dom';
import {
  DASHBOARD_SEARCH_PARAMS,
  QSR_FLOW_PARAMS,
  CHECKOUT_ROUTES,
} from 'constants/enums';
import { getDomain } from 'utilities/environment';
import { canUseDashboardMode } from 'utilities/dashboard_mode';
import { buildCartLink } from 'utilities/links';
import { replaceHistoryWithSearchParams } from 'components/pages/checkout/utilities';
import { COMPONENT_NAMES } from 'constants/tracking';
import { trackClickFeature } from 'utilities/tracking';

export const buildDashboardSearchParams = () => {
  // Set any additional modified shop params here
  const dashboardUrlParams = new URLSearchParams();

  dashboardUrlParams.append(
    DASHBOARD_SEARCH_PARAMS.COMPLETED_SETUP_TASK.KEY,
    DASHBOARD_SEARCH_PARAMS.COMPLETED_SETUP_TASK.VALUES.BUY_HARDWARE,
  );

  return dashboardUrlParams.toString()
    ? `?${dashboardUrlParams.toString()}`
    : '';
};

export const shouldShowBackButton = () => {
  // Add new sources here
  if (
    isSourceQsrHardwareFlow() &&
    canUseDashboardMode(window.location.pathname) &&
    !window.location.pathname.includes('/complete') // We should not go back to the QSR hardware flow after purchase
  ) {
    return true;
  }

  return false;
};

export const shouldShowDoneButton = () => {
  // Add new sources here
  if (
    isSourceQsrHardwareFlow() &&
    window.location.pathname.includes(CHECKOUT_ROUTES.COMPLETE)
  ) {
    return true;
  }

  return false;
};

const buildQsrFlowParams = () => {
  const qsrFlowParams = new URLSearchParams();

  qsrFlowParams.append(
    QSR_FLOW_PARAMS.FROM_GOLDEN_PATH_SETUP.KEY,
    QSR_FLOW_PARAMS.FROM_GOLDEN_PATH_SETUP.VALUES.TRUE,
  );
  qsrFlowParams.append(
    QSR_FLOW_PARAMS.SETUP_GUIDE_ITEM.KEY,
    QSR_FLOW_PARAMS.SETUP_GUIDE_ITEM.VALUES.BUY_HARDWARE,
  );
  qsrFlowParams.append(
    QSR_FLOW_PARAMS.STEP.KEY,
    QSR_FLOW_PARAMS.STEP.VALUES.BUY_HARDWARE,
  );

  return qsrFlowParams.toString() ? `?${qsrFlowParams.toString()}` : '';
};

const DashboardModeCompatiblePage = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  const [progress, setProgress] = useState(0);

  const doneButtonAction = () => {
    trackClickFeature({
      componentName: COMPONENT_NAMES.APPS.DASHBOARD_MODE.HEADER,
      eventDescription: 'Done clicked in dashboard mode',
      actionItem: 'done',
      additionalParameters: {
        currentPage: location.pathname,
      },
    });

    if (isSourceQsrHardwareFlow()) {
      // Give a little time for the tracking to fire before redirecting
      setTimeout(() => {
        window.location = `https://${getDomain()}/dashboard${buildDashboardSearchParams()}`;
      }, 80);
    }
  };

  const backButtonAction = () => {
    const currentPage = location.pathname;

    trackClickFeature({
      componentName: COMPONENT_NAMES.APPS.DASHBOARD_MODE.HEADER,
      eventDescription: 'Go back on in dashboard mode',
      actionItem: 'back',
      additionalParameters: {
        currentPage,
      },
    });

    // These hardcoded history overrides are necessary to ensure intuitive back button behavior through the QSR
    // hardware checkout flow, especially when sellers have to go through IDV at from cart and payment options.
    if (currentPage.includes(CHECKOUT_ROUTES.INFORMATION)) {
      // using window.location here as cart is a separate React app and the History API does not seem to work as expected
      // across apps
      window.location.replace(buildCartLink());
    } else if (currentPage.includes(CHECKOUT_ROUTES.PAYMENT_OPTION)) {
      replaceHistoryWithSearchParams(history, CHECKOUT_ROUTES.SHIPPING_METHOD);
    } else if (currentPage.includes(CHECKOUT_ROUTES.CART)) {
      window.location.replace(
        `https://${getDomain()}/dashboard/onboard-restaurants-flow${buildQsrFlowParams()}`,
      );
    } else if (currentPage.includes(CHECKOUT_ROUTES.SHIPPING_METHOD)) {
      replaceHistoryWithSearchParams(history, CHECKOUT_ROUTES.INFORMATION);
    } else {
      history.goBack();
    }
  };

  if (isDashboardMode()) {
    useEffect(() => {
      setProgress(
        convertPercentToDecimal(
          getProgressPercentageNumber() || getInitialProgressPercentageNumber(),
        ),
      );
    }, [location]);

    return (
      <>
        <DashboardModeHeader
          progress={progress}
          showBackButton={shouldShowBackButton()}
          backButtonAction={backButtonAction}
          showDoneButton={shouldShowDoneButton()}
          doneButtonAction={doneButtonAction}
        />
        <Box
          sx={{
            marginLeft: 'auto',
            marginRight: 'auto',
            maxWidth: '1680px',
          }}
        >
          {children}
        </Box>
      </>
    );
  }

  return children;
};

export default DashboardModeCompatiblePage;
